import axios from 'axios'
import { useAuthStore } from '@/stores/authStore'
import { authService } from '@/services/authService'

const API_URL = import.meta.env.VITE_API_URL

export const axiosInstance = axios.create({
  baseURL: API_URL + '/api/v1',
})

// Request interceptor for adding auth token
axiosInstance.interceptors.request.use((config) => {
  const accessToken = useAuthStore.getState().auth.accessToken
  if (accessToken && !config.headers?.Authorization) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

// Response interceptor for handling authentication errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 401 errors - redirect to login
    if (error.response?.status === 401) {
      authService.logout()
      window.location.href = '/sign-in'
    }

    return Promise.reject(error)
  }
)

// Response interceptor for handling token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as { url?: string; _retry?: boolean; headers?: Record<string, string> }

    // Don't try to refresh if this is already a refresh token request
    const isRefreshRequest = originalRequest.url?.includes('/user/refresh')

    // If we get a 401 and this is not a refresh request and we haven't retried yet
    if (
      error.response?.status === 401 &&
      !isRefreshRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      try {
        const { accessToken, refreshToken } = useAuthStore.getState().auth
        if (!refreshToken) {
          throw new Error('No refresh token available')
        }

        // Call refresh endpoint directly with axios to avoid circular deps and interceptors
        const refreshResponse = await axios.post(
          `${API_URL}/user/refresh`,
          { refreshToken },
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        )

        const payload = refreshResponse?.data
        const newAccessToken = payload?.access_token || ''
        if (newAccessToken) {
          // Update tokens
          useAuthStore.getState().auth.setTokens(
            newAccessToken,
            refreshToken // Keep existing refresh token if new one not provided
          )

          // Retry the original request with new token
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
          return axiosInstance(originalRequest)
        } else {
          throw new Error('Invalid refresh token response')
        }
      } catch (refreshError) {
        // eslint-disable-next-line no-console
        console.log('Token refresh failed:', refreshError)
        // Clear auth state and redirect to login
        authService.logout()
        window.location.href = '/sign-in'
        return Promise.reject(refreshError)
      }
    }

    // For all other errors or if refresh failed
    if (error.response?.status === 401) {
      authService.logout()
      window.location.href = '/sign-in'
    }

    return Promise.reject(error)
  }
)
